/**
 * @module AlgoliaRobots
 * @description Initializes and manages Algolia product search grid for robots.
 *
 * Uses the base product search grid component factory for consistency across product types.
 * This is specifically for product search grids, not general site search.
 */

import { createProductSearchGrid } from './BaseProductSearchGrid.js';
import { sortControllerSeries } from './controllerSeries.js';
import { createProductConfig } from '@js/algolia/createProductConfig.js';
import { robotsConfig } from '@js/config/algolia.js';

/**
 * Get array of specs to display for a robot
 * @param {Object} result - The search result
 * @param {Object} translations - Translation labels for spec names
 * @returns {Array} Array of spec objects with icon, label, value, and unit
 */
function getSpecs(result, translations = {}) {
  const specs = [];
  const maxPayloadLabel = translations.maxPayload || 'Max Payload';
  const reachLabel = translations.reach || 'Reach';

  if (result.maxPayload) {
    specs.push({
      icon: 'max-payload',
      label: maxPayloadLabel,
      value: result.maxPayload,
      unit: ' kg',
    });
  }

  if (result.reach) {
    specs.push({
      icon: 'reach',
      label: reachLabel,
      value: result.reach,
      unit: ' mm',
    });
  }
  return specs;
}

/**
 * Check if robot has any specs to display
 * @param {Object} result - The search result
 * @returns {boolean}
 */
function hasSpecs(result) {
  return !!(
    result.axes ||
    result.maxPayload ||
    result.reach ||
    result.repeatability
  );
}

/**
 * Format Algolia result to match product-card component structure
 *
 * This function remaps Algolia search result fields to match the structure expected by
 * the product-card component (_components/modules/product-card.twig). This allows the
 * Alpine.js template in robots-search.twig to use the same structure as the Twig component.
 *
 * Field mappings:
 * - result.title → card.heading
 * - result.url → card.url
 * - result.productImage → card.image
 * - result.robotType[0] → card.category
 * - result.tagline → card.description
 * - getSpecs(result) → card.attributes (array of {label, value, icon})
 * - result.legacy → card.legacy
 * - result.controllerSeries → card.controllerSeries (sorted, newest first)
 *
 * @param {Object} result - The raw Algolia search result object
 * @param {Object} translations - Translation labels for card attributes
 * @returns {Object} Formatted data matching product-card structure
 * @see templates/_components/modules/product-card.twig - Target component structure
 * @see templates/_partials/algolia/robots-search.twig - Where this is used
 */
function formatCardData(result, translations) {
  return {
    heading: result.title || '',
    url: result.absoluteUri || result.url || '#',
    image: result.productImage || null,
    category:
      result.robotType && result.robotType.length ? result.robotType[0] : null,
    description: result.description || result.tagline || null,
    attributes: getSpecs(result, translations).map((spec) => ({
      label: spec.label,
      value: spec.value + spec.unit,
      icon: spec.icon,
    })),
    legacy: result.legacy || false,
    controllerSeries: sortControllerSeries(result.controllerSeries),
  };
}

// Create and export the Alpine component using the base factory
export default createProductSearchGrid({
  appId: robotsConfig.app_id,
  apiKey: robotsConfig.api_key,
  indexName: robotsConfig.index,
  configureWidget: createProductConfig('[data-search-robots]'),
  formatCardData,
  getSpecs,
  hasSpecs,
  scrollToSelector: '[data-search-robots]',
});
